// =============================================================================
// web/src/features/diary/diaryZip.ts — T-326, hardened T-379.
//
// Client-side "Download all photos" — fetches each signed JPEG and bundles them
// into a single ZIP. Used by the internal diary tab + the public client portal.
//
// T-379 hardening (Nullarbor client reports: first an EMPTY zip, then a PARTIAL
// one). The old loop fetched each photo once, sequentially, and silently
// skipped ANY failure — so on a flaky connection the zip just quietly shrank.
// Now:
//   * limited-concurrency parallel fetch (faster on big sets),
//   * every photo gets up to 3 attempts with backoff,
//   * failures are REPORTED — a _MISSING_PHOTOS.txt goes in the zip and the
//     result tells the caller exactly what was skipped,
//   * an all-failures zip is never produced (throws 'none_fetched').
// =============================================================================

import JSZip from "jszip";

export interface DiaryZipPhoto { url: string; name: string }

export interface DiaryZipFailure { name: string; reason: string }

export interface DiaryZipResult {
  zipped: number;
  failed: DiaryZipFailure[];   // names + why (HTTP status / error) after retries
  total: number;
}

const CONCURRENCY = 4;
const ATTEMPTS = 3;

async function fetchWithRetry(url: string): Promise<{ blob: Blob | null; reason: string }> {
  let reason = "unknown";
  for (let i = 0; i < ATTEMPTS; i++) {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (res.ok) return { blob: await res.blob(), reason: "" };
      reason = `HTTP ${res.status}`;
      // 4xx won't heal (expired/missing object) — one retry is enough for those.
      if (res.status >= 400 && res.status < 500 && i >= 1) return { blob: null, reason };
    } catch (e: any) {
      // TypeError here = network/CORS-level failure (browser hides which).
      reason = `network/${e?.name ?? "error"}`;
    }
    await new Promise(r => setTimeout(r, 400 * (i + 1)));   // backoff 400/800ms
  }
  return { blob: null, reason };
}

/** Fetch all photos (retried, limited-parallel) and trigger a single ZIP download. */
export async function downloadDiaryPhotosZip(args: {
  filenameBase: string;          // e.g. SA000355_Nullarbor_RT_2026-06-29
  photos: DiaryZipPhoto[];
  onProgress?: (done: number, total: number) => void;
}): Promise<DiaryZipResult> {
  const zip = new JSZip();
  const failed: string[] = [];
  const seen = new Set<string>();
  let done = 0;

  // De-dupe filenames up front (ZIP entries must be unique).
  const jobs = args.photos.map((p, i) => {
    let name = p.name;
    if (seen.has(name)) {
      const dot = name.lastIndexOf(".");
      name = dot > 0 ? `${name.slice(0, dot)}_${i + 1}${name.slice(dot)}` : `${name}_${i + 1}`;
    }
    seen.add(name);
    return { url: p.url, name };
  });

  // Limited-concurrency worker pool.
  let next = 0;
  async function worker() {
    while (next < jobs.length) {
      const job = jobs[next++];
      const { blob, reason } = await fetchWithRetry(job.url);
      if (blob) zip.file(job.name, blob);
      else failed.push({ name: job.name, reason });
      done += 1;
      args.onProgress?.(done, jobs.length);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker));

  const zipped = jobs.length - failed.length;

  // Never hand over a zip with nothing in it.
  if (zipped === 0 && jobs.length > 0) throw new Error("none_fetched");

  // Make any gaps VISIBLE inside the zip itself — with the reason per photo,
  // so a screenshot of this file diagnoses the failure mode (403 = expired
  // link, 404 = file missing in storage, network/TypeError = CORS or drop).
  if (failed.length > 0) {
    zip.file(
      "_MISSING_PHOTOS.txt",
      `${failed.length} of ${jobs.length} photos could not be downloaded.\n` +
      `Re-run the download to fetch a complete set.\n\nMissing (with reason):\n` +
      failed.map(f => `  - ${f.name}  [${f.reason}]`).join("\n"),
    );
  }

  const out = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(out);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${args.filenameBase}_photos.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { zipped, failed, total: jobs.length };
}
